import { computeSalePaidPendingAmounts } from '../../shared/numeric/salePaymentAmounts';
import { CASHIER_LIST_FILTER_NULL } from './salesRepoSql';
import { salesListPaymentPendingSqlClause, salesListStatusSqlClause, salesListPaymentMethodSqlClause, salesListFiscalStatusSqlClause, salesListChannelSqlClause, salesListOrderByClause } from './salesListFilters';
import type { SaleListRow, SaleStatus, SaleChannel } from '../domain/salesTypes';
export type { SaleListRow };
import type { SaleStatusFilter, SaleFiscalStatusFilter, SaleSortKey, SaleSortDir } from '../domain/salesTypes';
import type { SaleChannelFilter } from './salesListFilters';
import { buildAccentInsensitiveSqlExpression, normalizeSearchText } from '../../shared/format/searchText';

/**
 * ADR-017 Fase 4: SQLite eliminado como path de datos operacional.
 * `db` es un stub que retorna `[]`/`undefined` en todas las plataformas.
 * Estas funciones son fallback offline para `fetchRemoteListSales` (Supabase);
 * sin SQLite local, retornan vacío/cero — equivalente al comportamiento del stub.
 */

export function parsePaymentMethodIds(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

const SALES_LIST_SLIM_SELECT = `s.id, s.branch_id, s.total, s.created_at, s.synced, s.payment_status,
           COALESCE(s.status, 'active') AS status, s.cashier_user_id,
           COALESCE(s.fiscal_status, 'pending_sync') AS fiscal_status,
           COALESCE(s.fiscal_mode, 'fiscal') AS fiscal_mode,
           COALESCE(s.channel, 'pos') AS channel,
           s.voided_at, s.void_reason, s.voided_by_user_id,
           s.dining_table_id, s.invoice_cae, s.invoice_status, s.invoice_issued_at,
           (SELECT COUNT(*) FROM sale_items si WHERE si.sale_id = s.id) AS item_count,
           (SELECT IFNULL(NULLIF(TRIM(si0.label), ''), si0.product_id)
            FROM sale_items si0 WHERE si0.sale_id = s.id ORDER BY si0.id LIMIT 1) AS first_item_label,
           (SELECT GROUP_CONCAT(DISTINCT sp.method_id)
            FROM sale_payments sp WHERE sp.sale_id = s.id) AS payment_method_ids_csv,
           (SELECT sp.customer_id FROM sale_payments sp
            WHERE sp.sale_id = s.id AND sp.customer_id IS NOT NULL LIMIT 1) AS fiado_customer_id,
           (SELECT c.name
            FROM customers c, sale_payments sp
            WHERE c.id = sp.customer_id AND sp.sale_id = s.id AND sp.customer_id IS NOT NULL
            LIMIT 1) AS fiado_customer_name,
           s.customer_id AS customer_id,
           (SELECT c.name FROM customers c WHERE c.id = s.customer_id LIMIT 1) AS customer_name,
           (SELECT COALESCE(SUM(sp.amount), 0) FROM sale_payments sp
            WHERE sp.sale_id = s.id AND sp.method_id != 'fiado'
              AND COALESCE(sp.status, 'confirmed') = 'confirmed') AS non_fiado_paid,
           (SELECT COALESCE(SUM(spa.amount), 0) FROM sale_payment_allocations spa
            WHERE spa.sale_id = s.id AND spa.voided_at IS NULL) AS fiado_allocation_paid`;

function mapSaleListRowFromSql(raw: {
  id: string;
  branch_id: string;
  total: number;
  created_at: string;
  synced: number;
  payment_status: string | null;
  status: string | null;
  cashier_user_id: string | null;
  fiscal_status: string | null;
  fiscal_mode: string | null;
  channel: string | null;
  voided_at: string | null;
  void_reason: string | null;
  voided_by_user_id: string | null;
  invoice_cae: string | null;
  invoice_status: string | null;
  invoice_issued_at: string | null;
  item_count: number;
  first_item_label: string | null;
  payment_method_ids_csv: string | null;
  fiado_customer_id: string | null;
  fiado_customer_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  dining_table_id: string | null;
  non_fiado_paid: number;
  fiado_allocation_paid: number;
}): SaleListRow {
  const paymentStatus = raw.payment_status ?? 'pending';
  const total = Number(raw.total ?? 0);
  const nonCustomerPaid = Number(raw.non_fiado_paid ?? 0);
  const customerAllocationPaid = Number(raw.fiado_allocation_paid ?? 0);
  const { paidAmount, pendingAmount } = computeSalePaidPendingAmounts(
    total,
    paymentStatus,
    nonCustomerPaid,
    customerAllocationPaid
  );

  return {
    id: raw.id,
    branchId: raw.branch_id,
    total,
    createdAt: raw.created_at || new Date(0).toISOString(),
    synced: !!raw.synced,
    paymentStatus,
    fiscalStatus: raw.fiscal_status ?? 'pending_sync',
    fiscalMode: raw.fiscal_mode ?? 'fiscal',
    status: (raw.status === 'voided' ? 'voided' : 'active') as SaleStatus,
    channel: (raw.channel === 'online' ? 'online' : 'pos') as SaleChannel,
    customerOrderId: null,
    voidedAt: raw.voided_at ?? null,
    voidReason: raw.void_reason ?? null,
    voidedByUserId: raw.voided_by_user_id ?? null,
    itemCount: Number(raw.item_count ?? 0),
    firstItemLabel: raw.first_item_label ?? null,
    paymentMethodIds: parsePaymentMethodIds(raw.payment_method_ids_csv),
    cashierUserId: raw.cashier_user_id ?? null,
    fiadoCustomerId: raw.fiado_customer_id ?? null,
    fiadoCustomerName: raw.fiado_customer_name ?? null,
    customerId: raw.customer_id ?? null,
    customerName: raw.customer_name ?? null,
    diningTableId: raw.dining_table_id ?? null,
    paidAmount,
    pendingAmount,
    invoiceCae: raw.invoice_cae ?? null,
    invoiceIssuedAt: raw.invoice_issued_at ?? null,
    invoiceStatus: raw.invoice_status ?? null,
  };
}

export type SalesListFilterParams = {
  branchId: string;
  fromIso: string;
  toIso: string;
  search?: string;
  cashierUserIdFilter?: string;
  paymentPendingOnly?: boolean;
  statusFilter?: SaleStatusFilter;
  paymentMethodFilter?: string;
  fiscalStatusFilter?: SaleFiscalStatusFilter;
  channelFilter?: SaleChannelFilter;
  sortKey?: SaleSortKey;
  sortDir?: SaleSortDir;
};

type SaleListSqlRow = {
  id: string;
  branch_id: string;
  total: number;
  created_at: string;
  synced: number;
  payment_status: string | null;
  status: string | null;
  cashier_user_id: string | null;
  fiscal_status: string | null;
  fiscal_mode: string | null;
  channel: string | null;
  voided_at: string | null;
  void_reason: string | null;
  voided_by_user_id: string | null;
  invoice_cae: string | null;
  invoice_status: string | null;
  invoice_issued_at: string | null;
  item_count: number;
  first_item_label: string | null;
  payment_method_ids_csv: string | null;
  fiado_customer_id: string | null;
  fiado_customer_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  dining_table_id: string | null;
  non_fiado_paid: number;
  fiado_allocation_paid: number;
};

const SALES_LIST_SEARCH_CONDITION = `(
              instr(${buildAccentInsensitiveSqlExpression('s.id')}, ?) > 0
              OR instr(${buildAccentInsensitiveSqlExpression('CAST(s.total AS TEXT)')}, ?) > 0
              OR instr(${buildAccentInsensitiveSqlExpression("IFNULL(si.label, '')")}, ?) > 0
              OR instr(${buildAccentInsensitiveSqlExpression("IFNULL(si.sku, '')")}, ?) > 0
            )`;

const SALES_LIST_SEARCH_JOINS = `LEFT JOIN sale_items si ON si.sale_id = s.id
           LEFT JOIN sale_payments sp ON sp.sale_id = s.id`;

function buildSalesListClauses(params: SalesListFilterParams, extraClause = '') {
  const cf = params.cashierUserIdFilter;
  const cashierClause =
    cf === undefined
      ? ''
      : cf === CASHIER_LIST_FILTER_NULL
        ? ' AND s.cashier_user_id IS NULL'
        : ' AND s.cashier_user_id = ?';
  const pendingClause = salesListPaymentPendingSqlClause(params.paymentPendingOnly);
  const statusClause = salesListStatusSqlClause(params.statusFilter);
  const methodClause = salesListPaymentMethodSqlClause(params.paymentMethodFilter);
  const fiscalClause = salesListFiscalStatusSqlClause(params.fiscalStatusFilter);
  const channelClause = salesListChannelSqlClause(params.channelFilter);
  return `${extraClause}${cashierClause}${pendingClause}${statusClause}${methodClause}${fiscalClause}${channelClause}`;
}

function buildSalesListBinds(params: SalesListFilterParams, search: string): unknown[] {
  const { branchId, fromIso, toIso } = params;
  const cf = params.cashierUserIdFilter;
  const bind: unknown[] = [branchId, fromIso, toIso];
  if (cf !== undefined && cf !== CASHIER_LIST_FILTER_NULL) bind.push(cf);
  if (params.paymentMethodFilter) bind.push(params.paymentMethodFilter);
  if (search) {
    const q = normalizeSearchText(search);
    bind.push(q, q, q, q);
  }
  return bind;
}

/**
 * ADR-017: no hay listado local. Llamar esto en un path activo es un bug —
 * el caller debe usar `fetchRemoteListSales` / fallar online.
 * // TECH DEBT: temporary throw stub hasta retirar merge/offline en useSalesListForBranch (Slice 2).
 */
export const listSales = async (_params: {
  branchId: string;
  fromIso: string;
  toIso: string;
  search?: string;
  limit: number;
  offset: number;
  cashierUserIdFilter?: string;
  paymentPendingOnly?: boolean;
  statusFilter?: SaleStatusFilter;
  paymentMethodFilter?: string;
  fiscalStatusFilter?: SaleFiscalStatusFilter;
  channelFilter?: SaleChannelFilter;
  sortKey?: SaleSortKey;
  sortDir?: SaleSortDir;
}): Promise<{ rows: SaleListRow[]; hasMore: boolean }> => {
  throw new Error('El historial de ventas es solo online. Conectate e intentá de nuevo.');
};

export const countSales = async (_params: SalesListFilterParams): Promise<number> => {
  throw new Error('El conteo de ventas es solo online. Conectate e intentá de nuevo.');
};
